/**
 * Admin endpoints for managing webhook subscriptions
 * (/api/admin/webhooks*). Gated server-side on `users.manage`.
 */
import { apiFetch } from "./apiClient";

// Keep in sync with mttr/app_api/routes/webhooks.py:ALL_WEBHOOK_EVENT_TYPES
// + mttr/webhook/evaluator.py.
export const ALL_WEBHOOK_EVENT_TYPES = [
  "inventory.stockout.opened",
  "inventory.stockout.closed",
] as const;

export type WebhookEventType = (typeof ALL_WEBHOOK_EVENT_TYPES)[number];

export const WEBHOOK_EVENT_LABEL: Record<WebhookEventType, string> = {
  "inventory.stockout.opened": "Stockout opened",
  "inventory.stockout.closed": "Stockout closed",
};

export interface WebhookSubscriptionRow {
  subscription_id: number;
  company_id: number;
  name: string;
  url: string;
  // Empty list = all event types.
  event_types: string[];
  created_by: string;
  created_utc: string | null;
  disabled_utc: string | null;
  disabled_by: string | null;
  last_success_utc: string | null;
  last_failure_utc: string | null;
  consecutive_failures: number;
}

export interface WebhookCreateResponse {
  subscription_id: number;
  name: string;
  company_id: number;
  url: string;
  event_types: string[];
  secret: string;
  warning: string;
}

export interface WebhookDeliveryRow {
  delivery_id: number;
  event_type: string;
  url: string;
  attempt: number;
  success: boolean;
  http_status: number | null;
  error_message: string | null;
  attempted_utc: string | null;
  payload_json: string;
}

async function req<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  return apiFetch<T>(path, token, init);
}

export function listWebhooks(
  token: string, companyId?: number,
): Promise<{ subscriptions: WebhookSubscriptionRow[] }> {
  const qs = companyId != null ? `?company_id=${companyId}` : "";
  return req(token, `/api/admin/webhooks${qs}`);
}

export function createWebhook(
  token: string,
  body: { name: string; url: string; company_id?: number; event_types?: string[] },
): Promise<WebhookCreateResponse> {
  return req(token, "/api/admin/webhooks", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function disableWebhook(
  token: string, subscriptionId: number,
): Promise<{ disabled: boolean }> {
  return req(token, `/api/admin/webhooks/${subscriptionId}/disable`, {
    method: "POST",
    body: "{}",
  });
}

export function testWebhook(
  token: string, subscriptionId: number,
): Promise<{
  delivery_id: number; success: boolean;
  http_status: number | null; error_message: string | null;
}> {
  return req(token, `/api/admin/webhooks/${subscriptionId}/test`, {
    method: "POST",
    body: "{}",
  });
}

export function listWebhookDeliveries(
  token: string, subscriptionId: number, limit = 50,
): Promise<{ deliveries: WebhookDeliveryRow[] }> {
  return req(token, `/api/admin/webhooks/${subscriptionId}/deliveries?limit=${limit}`);
}
